import { useModal } from '@ebay/nice-modal-react'
import { styled } from '@linaria/react'
import { lazy, useEffect, useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'

import type { User } from '../../store/slices/userSlice'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { PaymentChannel } from '../../types/wallet-management'
import { getAdsInfo } from '../../utils/ads-info'
import { api } from '../../utils/api-client'
import { getDeviceInfo } from '../../utils/device'
import { BannerPopupModal, ToastMessage } from '../common'
import MessageModal from '../common/message-modal'
import { TailSpin } from '../loaders'
import AmountSelector from '../payment-form/amount-selector'
import type { Promo } from '../payment-form/promotion-input'
import PromotionInput from '../payment-form/promotion-input'
import PromotionTermsModal from '../payment-form/promotion-terms-modal'
import TermsCheckbox from '../payment-form/terms-checkbox'
import AboutDeposit from './about-deposit'
import AmountInput from './amount-input'
import ConfirmButton from './confirm-button'
import { random } from '../../utils/collection'
import { debounce } from '../../utils/common'

interface PromoCodeResponse {
  data: {
    id: string
    promo_code: string
    spagent_id: string
    content: string
    bonus_amount: number
    total_credit: number
  }
}

export type PromoType = 'custom' | 'autopromo' | null

const DepositForm = ({ addBankBtn}: { addBankBtn?: React.ReactNode }) => {
  const email = useBoundStore(state => state.user.email)
  const phone = useBoundStore(state => state.user.phone)
  const selectedPaymentChannel = useBoundStore(
    state => state.payment.selectedPaymentChannel,
  )
  const setUser = useBoundStore(state => state.user.setUser)
  const promotionTermsModal = useModal(PromotionTermsModal)
  const messageModal = useModal(MessageModal)
  const bannerPopupModal = useModal(BannerPopupModal)
  const toastMessage = useModal(ToastMessage)
  const {
    t,
    i18n: { language },
  } = useTranslation('walletManagement')
  const [amount, setAmount] = useState<number>(500)
  const [selectedPromo, setSelectedPromo] = useState<Promo | null>(null)
  const [customPromoCode, setCustomPromoCode] = useState('')
  const [promoList, setPromoList] = useState<Promo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isReadTerms, setIsReadTerms] = useState(false)
  const [promoType, setPromoType] = useState<PromoType>(null)
  const [showPromoCode, setShowPromoCode] = useState(false)
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null)

  useEffect(() => {
    if (showPromoCode) return

    const controller = new AbortController()
    const signal = controller.signal

    api
      .post<
        ApiResponse<Array<Omit<Promo, 'label'> & { title: string }>> & {
          show_promocode: 0 | 1
        }
      >(
        '/payment.php',
        {
          type: 'autopromotion_list',
          amount: amount,
        },
        { signal },
      )
      .then(res => {
        const { status, info, show_promocode } = res.data
        if (status === 1 && info) {
          setShowPromoCode(show_promocode === 1 ? true : false)
          const formatInfo = info.map(item => ({
            label: item.title,
            ...item,
          }))
          if (selectedPromo) {
            setSelectedPromo(prev => {
              const samePromo = formatInfo.find(
                promo => (prev as Promo).id === promo.id,
              )
              if (samePromo) {
                if (samePromo.disabled) {
                  return null
                }
                return samePromo
              }
              return formatInfo[0]
            })
          } else {
            const firstDepositBonus = formatInfo.find(
              promo => [234, 235].includes(+promo.id) && !promo.disabled,
            )
            if (firstDepositBonus) {
              bannerPopupModal.show({
                imgUrl: `https://www.u2d8899.com/mexicoimages/banner/100_bonus_${language}.webp`,
              })
            }
          }
          setPromoList(formatInfo)
        } else {
          setSelectedPromo(null)
          setPromoList([])
        }
      })

    return () => {
      controller.abort()
    }
  }, [amount, showPromoCode])

  const onSubmit = async () => {
    if (!selectedPaymentChannel) return
    const reqBody = {
      type: 'submitpay',
      amount: amount,
      pay_type: 1,
      line_type: selectedPaymentChannel.line_type,
      promo_code: customPromoCode,
      email,
      meta: getDeviceInfo(),
      ads_info: getAdsInfo(),
      pay_id: selectedPaymentChannel.id,
      autopromo: promoType === 'autopromo' ? selectedPromo?.id : '',
    }

    setIsLoading(true)
    const response = await api.post<
      ApiResponse<{ form: string; message: string; data: { billno: string } }>
    >('/payment.php', reqBody)
    const { status, info } = response.data

    if (status === 1 || status === 2) {
      // GA4 Caramelo
      TagManager.dataLayer({
        dataLayer: {
          event: 'begin_checkout',
          email,
          transaction_id: info.data.billno,
          value: +amount,
          currency: 'MXN',
          items: [
            {
              item_id: selectedPaymentChannel.id,
              item_name: selectedPaymentChannel.show_name,
              price: +amount,
              quantity: '1',
              currency: 'MXN',
            },
          ],
        },
      })

      fbq('track', 'InitiateCheckout', {
        value: Number(amount),
        currency: 'MXN',
      })

      // Fortune Conversion Tracking
      fbq('track', 'Purchase', {
        value: random(100, 5000), // random value between 100 and 5000 no need to be exact value
        currency: 'MXN',
      })

      // Twitter
      twq('event', 'tw-olnlq-olnmq', {
        value: amount,
        currency: 'MXN',
        contents: [
          {
            transaction_id: info.data.billno,
            platform_id: selectedPaymentChannel.id,
            platform: selectedPaymentChannel.show_name,
            promotion: selectedPromo?.label ?? null,
            promotion_id: selectedPromo?.id ?? null,
          },
        ],
        email_address: email,
        phone_number: `+52${phone}`,
      })

      if (selectedPaymentChannel.need_bank_card === 1) {
        setUser({
          pending_bind: 0,
          pending_amount: amount.toString(),
        } as User)
      }

      const submitForm = () => {
        const form = document.getElementById('pay_form') as HTMLFormElement
        if (form) {
          form.remove()
        }
        document
          .getElementById('root')
          ?.insertAdjacentHTML('beforeend', info.form)
        const newForm = document.getElementById('pay_form') as HTMLFormElement
        newForm?.submit()
      }

      if (status === 2) {
        messageModal.show({
          message: info.message,
          onClick: submitForm,
        })
      } else {
        submitForm()
      }
    } else {
      if (typeof info === 'string')
        messageModal.show({
          title: '',
          message: info,
        })
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (!showPromoCode || !customPromoCode) return

    const controller = new AbortController()
    const signal = controller.signal
    const fetchCustomPromo = debounce(async () => {
      if (customPromoCode) {
        setPromoCodeError(null)
        const response = await api.post<ApiResponse<PromoCodeResponse>>(
          '/payment.php',
          {
            type: 'promo_code',
            promo_code: customPromoCode,
            amount,
          },
          { signal },
        )
        const { status, info } = response.data
        if (status === 1 && typeof info === 'object') {
          const { promo_code, total_credit, ...rest } = info.data
          setSelectedPromo({
            ...rest,
            label: promo_code,
            total_credit_amount: total_credit,
          })
        } else if (typeof info === 'string') {
          setPromoCodeError(info)
        }
      } else {
        setSelectedPromo(null)
      }
    }, 500)

    fetchCustomPromo()
    return () => {
      controller.abort()
    }
  }, [customPromoCode, amount])

  const onPromoSelect = (selected: Promo) => {
    setIsReadTerms(false)
    if (selected.disabled_reason) {
      toastMessage.show({ text: selected.disabled_reason, isValid: false })
      return
    }
    setSelectedPromo(selected.id === selectedPromo?.id ? null : selected)
  }

  const renderButton = () => {
    if (!selectedPaymentChannel) return null
    if (isLoading)
      return (
        <div className="flx-ctr" style={{ height: '45px' }}>
          <TailSpin width={39} height={39} color="#fff" />
        </div>
      )

    return (
      <ConfirmButton
        disabled={
          !+amount ||
          +amount < selectedPaymentChannel.pay_limit.minimum ||
          +amount > selectedPaymentChannel.pay_limit.maximum ||
          (!isReadTerms &&
            promoType === 'autopromo' &&
            selectedPromo?.id !== 0) ||
          (promoType === 'custom' && (!isReadTerms || !!promoCodeError))
        }
        onClick={onSubmit}>
        {t('confirm')}
      </ConfirmButton>
    )
  }

  const inputError = useMemo(() => {
    if (!selectedPaymentChannel) return ''
    if (+amount === 0 || +amount < selectedPaymentChannel.pay_limit.minimum)
      return `*${t('minimum-deposit')} ${
        selectedPaymentChannel.pay_limit.minimum
      }.`
    if (+amount > selectedPaymentChannel.pay_limit.maximum)
      return `*${t('maximum-deposit')} ${
        selectedPaymentChannel.pay_limit.maximum
      }.`
    return ''
  }, [amount, selectedPaymentChannel])

  if (!selectedPaymentChannel) return null

  const showPromoTermsModal = () => {
    selectedPromo &&
      promotionTermsModal.show({
        htmlContent: selectedPromo.content,
        promoId: selectedPromo.id.toString(),
      })
  }

  const handleCustomPromoChange = (promo: string) => {
    setPromoCodeError(null)
    setSelectedPromo(null)
    setCustomPromoCode(promo)
    if (promoList.length === 0) {
      setPromoType(promo ? 'custom' : null)
    }
  }

  return (
    <>
      {selectedPaymentChannel && (
        <>
          <AmountInput
            page={t('deposit-amount')}
            tooltip={<AboutDeposit />}
            amount={amount}
            setAmount={setAmount}
            error={inputError}
          />
          <AmountSelector
            amounts={selectedPaymentChannel.fixed_amount}
            amount={amount}
            setAmount={setAmount}
          />
        </>
      )}
      <PromotionInput
        onPromoSelect={onPromoSelect}
        customPromoCode={customPromoCode}
        onCustomPromoChange={handleCustomPromoChange}
        selectedPromo={selectedPromo}
        promoList={promoList}
        showPromoCode={showPromoCode}
        promoCodeError={promoCodeError}
        promoType={promoType}
        setPromoType={setPromoType}
      />
      {selectedPromo && selectedPromo.id !== 0 && (
        <TermsCheckbox
          isChecked={isReadTerms}
          setIsChecked={setIsReadTerms}
          onClick={showPromoTermsModal}
        />
      )}
      {addBankBtn}
      {renderButton()}
    </>
  )
}

export default DepositForm
